import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  FeatureName,
  cacheKeys,
  useIsFeatureEnabled,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import {
  AssetHeader,
  Colors,
  LoadingIndicator,
  RoutineExecutionProps,
  SlideAction,
  getAutoPromptProps,
  useResponsiveDesign,
  useSlideLayout,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useLocation, useNavigate } from 'react-router-dom';
import { RoutinesAssignedListModal } from '../../routines/components/routinesAssignedListModal';
import { MediaPicker } from '../../shared/components/media/mediaPicker';
import { isActivityOrEditPage } from '../assetUtils';
import { AssetActivityFold } from './assetActivityFold';
import { AssetCard as AssetCardOld } from './assetCard-old';
import { AssetMediaFold } from './assetMediaFold';
import { AssetRoutineFold } from './assetRoutineFold';

export const AssetCard = ({ assetId, pageSize }: { assetId: string, pageSize?: number }) => {
  const isCropCircleEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  if (isCropCircleEnabled) {
    return <AssetCardBase assetId={assetId} />;
  }

  return <AssetCardOld assetId={assetId} />;
};

export const AssetCardBase = ({ assetId }: { assetId: string }) => {
  const { onSlideChange, setColumnTitle } = useSlideLayout();
  const location = useLocation();
  const [selectedIssueId, setSelectedIssueId] = useState<string | undefined>(
    undefined
  );
  const { t } = useTranslation();
  const {
    workspaceAsset,
    isLoading: isLoadingWorkspaceAsset,
    invalidateQuery: invalidateWorkspaceAsset,
  } = useWorkspaceAsset(assetId);
  const navigate = useNavigate();
  const isResponsiveDesign = useResponsiveDesign();
  const [routinesModalOpened, setRoutinesModalOpened] = useState(false);
  const [initialRoutine, setInitialRoutine] = useState<
    RoutineExecutionProps | undefined
  >();
  const hasAutoPrompted = useRef(false);
  const queryClient = useQueryClient();
  const isEditPage = isActivityOrEditPage(location);

  // Reset component state when assetId changes
  useEffect(() => {
    hasAutoPrompted.current = false;
    invalidateWorkspaceAsset();
  }, [assetId]);

  // Auto-prompt routines on asset open
  useEffect(() => {
    if (isLoadingWorkspaceAsset || !workspaceAsset || hasAutoPrompted.current)
      return;

    if (workspaceAsset.asset?.id !== assetId) return;

    if (!isEditPage) {
      const autoPrompt = getAutoPromptProps(workspaceAsset);

      if (autoPrompt) {
        hasAutoPrompted.current = true;
        setInitialRoutine(autoPrompt.first);
        setRoutinesModalOpened(true);
      }
    }
  }, [isLoadingWorkspaceAsset, workspaceAsset, assetId, isEditPage]);

  useEffect(() => {
    if (workspaceAsset) {
      setColumnTitle(workspaceAsset.asset?.name || 'assets.menu.title');
    }
  }, [workspaceAsset, setColumnTitle]);

  const invalidateIssues = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: cacheKeys('issue').all });
  }, [queryClient]);

  const onRoutineCompleted = useCallback(async () => {
    setRoutinesModalOpened(false);
    setInitialRoutine(undefined);
    await invalidateWorkspaceAsset();
    await invalidateIssues();
  }, [invalidateIssues, invalidateWorkspaceAsset]);

  const onIssueSelected = async (issueId: string) => {
    setSelectedIssueId(issueId);

    if (!isActivityOrEditPage(location)) {
      await onSlideChange(SlideAction.NEXT);
    }

    navigate(`/assets/${assetId}/activity/${issueId}`);
  };

  const onAssetEdit = async () => {
    if (!isActivityOrEditPage(location)) {
      await onSlideChange(SlideAction.NEXT);
    }

    navigate(`/assets/${assetId}/edit`);
  };

  const viewAssetInfoLink = !location.pathname.includes('edit') ? (
    <Pressable style={styles.viewAssetButton} onPress={onAssetEdit}>
      <Text style={styles.viewAssetButtonLabel}>
        {`${t('topNav.moreAssetInfo')}`}
      </Text>
      <MaterialCommunityIcons
        size={24}
        color={Colors.primaryDark}
        name="chevron-right"
        style={{
          marginLeft: -4,
          textAlign: 'justify',
        }}
      />
    </Pressable>
  ) : null;

  if (isLoadingWorkspaceAsset) {
    return <LoadingIndicator />;
  }

  return (
    workspaceAsset !== undefined && (
      <View style={styles.container}>
        <View style={styles.containerHeader}>
          <AssetHeader
            workspaceAsset={workspaceAsset!}
            additionalHeaderBlock={viewAssetInfoLink}
            assetDetailsContainerStyle={styles.assetDetailsContainerStyle}
            imgSize={isResponsiveDesign ? 85 : 112}
            invalidateQuery={async () => await invalidateWorkspaceAsset()}
            mediaPicker={MediaPicker}
          />
        </View>
        <AssetRoutineFold assetId={assetId} />
        <AssetActivityFold
          assetId={assetId}
          onIssueSelected={onIssueSelected}
          selectedIssue={selectedIssueId}
          showAssetName={false}
        />
        <RoutinesAssignedListModal
          isOpened={routinesModalOpened}
          onCompleteRoutines={onRoutineCompleted}
          onCancel={() => {
            setRoutinesModalOpened(false);
            setInitialRoutine(undefined);
          }}
          workspaceAsset={workspaceAsset!}
          initialRoutine={initialRoutine}
        />
        <AssetMediaFold workspaceAsset={workspaceAsset} assetId={assetId} />
      </View>
    )
  );
};

const styles = StyleSheet.create({
  container: {
    paddingBottom: 50,
  },
  containerHeader: {
    paddingTop: 6,
    paddingHorizontal: 16,
    backgroundColor: Colors.white,
  },
  assetDetailsContainerStyle: {
    height: 112,
    justifyContent: 'space-between',
  },
  viewAssetButton: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    height: 24,
    marginTop: 5,
    paddingVertical: 0,
    marginVertical: 0,
  },
  viewAssetButtonLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: Colors.primaryDark,
    lineHeight: 19,
    marginRight: 0,
    padding: 0,
  },
});
